/*
 * @bot-written
 *
 * WARNING AND NOTICE
 * Any access, download, storage, and/or use of this source code is subject to the terms and conditions of the
 * Full Software Licence as accepted by you before being granted access to this source code and other materials,
 * the terms of which can be accessed on the Codebots website at https://codebots.com/full-software-licence. Any
 * commercial use in contravention of the terms of the Full Software Licence may be pursued by Codebots through
 * licence termination and further legal action, and be required to indemnify Codebots for any loss or damage,
 * including interest and costs. You are deemed to have accepted the terms of the Full Software Licence on any
 * access, download, storage, and/or use of this source code.
 *
 * BOT WARNING
 * This file is bot-written.
 * Any changes out side of "protected regions" will be lost next time the bot makes any changes.
 */
import moment from 'moment';
import { action, observable } from 'mobx';
import {
	Model,
	IModelAttributes,
	attribute,
	entity,
	ReferencePath,
} from 'Models/Model';
import { IOrderByCondition } from 'Views/Components/ModelCollection/ModelQuery';
import * as Models from 'Models/Entities';
import * as Validators from 'Validators';
import { CRUD } from '../CRUDOptions';
import * as AttrUtils from 'Util/AttributeUtils';
import { IAcl } from 'Models/Security/IAcl';
import {
	getCreatedModifiedCrudOptions,
} from 'Util/EntityUtils';
import VisitorsInjectableFrequencyEntity from 'Models/Security/Acl/VisitorsInjectableFrequencyEntity';
import AgencyAdminInjectableFrequencyEntity from 'Models/Security/Acl/AgencyAdminInjectableFrequencyEntity';
import ApplicationUserInjectableFrequencyEntity from 'Models/Security/Acl/ApplicationUserInjectableFrequencyEntity';
import AgencyPlaylisterInjectableFrequencyEntity from 'Models/Security/Acl/AgencyPlaylisterInjectableFrequencyEntity';
import MustardAdminInjectableFrequencyEntity from 'Models/Security/Acl/MustardAdminInjectableFrequencyEntity';
import MustardManagementInjectableFrequencyEntity from 'Models/Security/Acl/MustardManagementInjectableFrequencyEntity';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import SuperAdministratorScheme from '../Security/Acl/SuperAdministratorScheme';
// % protected region % [Add any further imports here] off begin
// % protected region % [Add any further imports here] end

export interface IInjectableFrequencyEntityAttributes extends IModelAttributes {
	frequency: number;
	start: Date;
	end: Date;

	moodId: string;
	mood: Models.MoodEntity | Models.IMoodEntityAttributes;
	// % protected region % [Add any custom attributes to the interface here] off begin
	// % protected region % [Add any custom attributes to the interface here] end
}

// % protected region % [Customise your entity metadata here] off begin
@entity('InjectableFrequencyEntity', 'Injectable Frequency')
// % protected region % [Customise your entity metadata here] end
export default class InjectableFrequencyEntity extends Model
	implements IInjectableFrequencyEntityAttributes {
	public static acls: IAcl[] = [
		new SuperAdministratorScheme(),
		new VisitorsInjectableFrequencyEntity(),
		new AgencyAdminInjectableFrequencyEntity(),
		new ApplicationUserInjectableFrequencyEntity(),
		new AgencyPlaylisterInjectableFrequencyEntity(),
		new MustardAdminInjectableFrequencyEntity(),
		new MustardManagementInjectableFrequencyEntity(),
		// % protected region % [Add any further ACL entries here] off begin
		// % protected region % [Add any further ACL entries here] end
	];

	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [
		// % protected region % [Add any custom create exclusions here] off begin
		// % protected region % [Add any custom create exclusions here] end
	];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [
		// % protected region % [Add any custom update exclusions here] off begin
		// % protected region % [Add any custom update exclusions here] end
	];

	/**
	 * The default order by field when the collection is loaded .
	 */
	public get orderByField(): IOrderByCondition<Model> | undefined {
		// % protected region % [Modify the order by field here] off begin
		return {
			path: 'start',
			descending: false,
		};
		// % protected region % [Modify the order by field here] end
	}

	// % protected region % [Modify props to the crud options here for attribute 'Frequency'] off begin
	@Validators.Required()
	@Validators.Integer()
	@observable
	@attribute<InjectableFrequencyEntity, number>()
	@CRUD({
		name: 'Frequency',
		displayType: 'textfield',
		order: 10,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseInteger,
	})
	public frequency: number;
	// % protected region % [Modify props to the crud options here for attribute 'Frequency'] end

	// % protected region % [Modify props to the crud options here for attribute 'Start'] off begin
	@Validators.Required()
	@observable
	@attribute<InjectableFrequencyEntity, Date>({ serialise: (e, a) => AttrUtils.serialiseDate(a) })
	@CRUD({
		name: 'Start',
		displayType: 'datepicker',
		order: 20,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: attr => AttrUtils.standardiseDate(attr, { serialiseAs: 'date' }),
	})
	public start: Date;
	// % protected region % [Modify props to the crud options here for attribute 'Start'] end

	// % protected region % [Modify props to the crud options here for attribute 'End'] off begin
	@Validators.Required()
	@observable
	@attribute<InjectableFrequencyEntity, Date>({ serialise: (e, a) => AttrUtils.serialiseDate(a) })
	@CRUD({
		name: 'End',
		displayType: 'datepicker',
		order: 30,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: attr => AttrUtils.standardiseDate(attr, { serialiseAs: 'date' }),
	})
	public end: Date;
	// % protected region % [Modify props to the crud options here for attribute 'End'] end

	@Validators.Required()
	@observable
	@attribute()
	@CRUD({
		// % protected region % [Modify props to the crud options here for reference 'Mood'] off begin
		name: 'Mood',
		displayType: 'reference-combobox',
		order: 40,
		referenceTypeFunc: () => Models.MoodEntity,
		// % protected region % [Modify props to the crud options here for reference 'Mood'] end
	})
	public moodId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public mood: Models.MoodEntity;

	// % protected region % [Add any custom attributes to the model here] off begin
	// % protected region % [Add any custom attributes to the model here] end

	// eslint-disable-next-line @typescript-eslint/no-useless-constructor
	constructor(attributes?: Partial<IInjectableFrequencyEntityAttributes>) {
		// % protected region % [Add any extra constructor logic before calling super here] off begin
		// % protected region % [Add any extra constructor logic before calling super here] end

		super(attributes);

		// % protected region % [Add any extra constructor logic after calling super here] off begin
		// % protected region % [Add any extra constructor logic after calling super here] end
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	@action
	public assignAttributes(attributes?: Partial<IInjectableFrequencyEntityAttributes>) {
		// % protected region % [Override assign attributes here] off begin
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.frequency !== undefined) {
				this.frequency = attributes.frequency;
			}
			if (attributes.start !== undefined) {
				if (attributes.start === null) {
					this.start = attributes.start;
				} else {
					this.start = moment(attributes.start).toDate();
				}
			}
			if (attributes.end !== undefined) {
				if (attributes.end === null) {
					this.end = attributes.end;
				} else {
					this.end = moment(attributes.end).toDate();
				}
			}
			if (attributes.moodId !== undefined) {
				this.moodId = attributes.moodId;
			}
			if (attributes.mood !== undefined) {
				if (attributes.mood === null) {
					this.mood = attributes.mood;
				} else if (attributes.mood instanceof Models.MoodEntity) {
					this.mood = attributes.mood;
					this.moodId = attributes.mood.id;
				} else {
					this.mood = new Models.MoodEntity(attributes.mood);
					this.moodId = this.mood.id;
				}
			}
			// % protected region % [Override assign attributes here] end

			// % protected region % [Add any extra assign attributes logic here] off begin
			// % protected region % [Add any extra assign attributes logic here] end
		}
	}

	/**
	 * Additional fields that are added to GraphQL queries when using the
	 * the managed model APIs.
	 */
	// % protected region % [Customize Default Expands here] off begin
	public defaultExpands = `
		mood {
			${Models.MoodEntity.getAttributes().join('\n')}
		}
	`;
	// % protected region % [Customize Default Expands here] end

	/**
	 * The save method that is called from the admin CRUD components.
	 */
	// % protected region % [Customize Save From Crud here] off begin
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public async saveFromCrud(formMode: EntityFormMode) {
		const relationPath: ReferencePath = {
		};
		return this.save(
			relationPath,
			{
				options: [
					{
						key: 'mergeReferences',
						graphQlType: '[String]',
						value: [
						],
					},
				],
			},
		);
	}
	// % protected region % [Customize Save From Crud here] end

	/**
	 * Returns the string representation of this entity to display on the UI.
	 */
	public getDisplayName() {
		// % protected region % [Customise the display name for this entity] off begin
		return this.id;
		// % protected region % [Customise the display name for this entity] end
	}

	// % protected region % [Add any further custom model features here] off begin
	// % protected region % [Add any further custom model features here] end
}

// % protected region % [Modify the create and modified CRUD attributes here] off begin
/*
 * Retrieve the created and modified CRUD attributes for defining the CRUD views and decorate the class with them.
 */
const [createdAttr, modifiedAttr] = getCreatedModifiedCrudOptions();
CRUD(createdAttr)(InjectableFrequencyEntity.prototype, 'created');
CRUD(modifiedAttr)(InjectableFrequencyEntity.prototype, 'modified');
// % protected region % [Modify the create and modified CRUD attributes here] end
